<template>
  <global-modal name="event-modal-auto-passes" @before-open="beforeOpen">
    <Modal @close="$modal.hide('event-modal-auto-passes')" v-if="event">
      <div class="event-modal-auto-passes">
        <EventModalPassesHeader
          :options="header"
          @print="printAllPasses"
          @download="downloadAllPasses"
          :hide-quota="
            Number(getLocaleValue(exhibition, 'create_car_pass_disabled'))
          "
          :show-rules="isShowRules"
        >
          <template slot="title">
            {{ $t("event.passes.carPasses") }}
          </template>
          <template
            slot="pass-warning"
            v-if="getLocaleValue(exhibition, 'car_pass_info_1').length"
          >
            <span v-html="getLocaleValue(exhibition, 'car_pass_info_1')">
            </span>
          </template>
          <template
            slot="pass-warning2"
            v-if="getLocaleValue(exhibition, 'car_pass_info_2').length"
          >
            <span v-html="getLocaleValue(exhibition, 'car_pass_info_2')">
            </span>
          </template>
          <template slot="rules-title">
            {{ $t("event.passes.car.rulesTitle") }}
          </template>

          <template slot="rules-content">
            <DeliveryRules />
          </template>
        </EventModalPassesHeader>
        <div class="loader-wrap" v-if="isLoading">
          <Loader is-absolute white opacity />
        </div>

        <template
          v-else-if="
            !Number(getLocaleValue(exhibition, 'create_car_pass_disabled'))
          "
        >
          <EventModalPassesAddForm
            v-if="
              hasAccessToWrite &&
                (isAddFormShown || !autoPasses.length) &&
                diffInSeconds(this.passDeadline.car) >= 0
            "
            class="event-modal-auto-passes__add"
            :show-hide="!!autoPasses.length"
            :is-submitting="isAddFormSubmitting"
            :package-upload="false"
            :has-errors="errors.any()"
            @hide="isAddFormShown = false"
            @submit="submit"
          >
            <template slot="form-title">
              {{ $t("event.passes.car.addTitle") }}
            </template>

            <h2 class="event-modal-passes-add-form__title">
              {{ $t("event.passes.car.carApplicationTitle") }}
            </h2>
            <details
              v-if="getLocaleValue(exhibition, 'car_pass_info_3').length"
              class="attention"
              open
              style="margin-top: -10px; margin-bottom: 30px;"
            >
              <summary class="attention__summary">
                <span class="attention__summary-text">
                  {{ $t("participantTermsDocs.attentionNote.title") }}</span
                >
              </summary>

              <p>
                <span
                  class="text-description"
                  v-html="getLocaleValue(exhibition, 'car_pass_info_3')"
                >
                </span>
              </p>
            </details>

            <div class="event-modal-passes-add-form__row flex">
              <div class="event-modal-passes-add-form__row-col _380">
                <InputLabel>
                  <template #label>
                    {{ $t("event.passes.car.govVehicleNumber") }}
                  </template>

                  <the-mask
                    mask="FFFFFFFFFFFFFFFF"
                    :tokens="govTrailerNumberFilter"
                    type="text"
                    placeholder="X000XX00"
                    v-model="addFormData.govVehicleNumber"
                    name="govVehicleNumber"
                    v-validate="{
                      required: true,
                      min: 6,
                      regex: /^[A-Z0-9 -АВЕКМНОРСТУХ]*$/
                    }"
                  />

                  <template slot="error" v-if="errors.has('govVehicleNumber')">
                    {{
                      getValidateErrorWithoutName(
                        errors.first("govVehicleNumber"),
                        "govVehicleNumber"
                      )
                    }}
                  </template>
                </InputLabel>
              </div>

              <div class="event-modal-passes-add-form__row-col _380">
                <InputLabel>
                  <template #label>
                    {{ $t("event.passes.car.govTrailerNumber") }}
                  </template>

                  <the-mask
                    mask="FFFFFFFFFFFFFFFF"
                    :tokens="govTrailerNumberFilter"
                    type="text"
                    name="govTrailerNumber"
                    placeholder="XX0000"
                    v-model="addFormData.govTrailerNumber"
                    min="6"
                    v-validate="{
                      regex: /^[A-Z0-9 -АВЕКМНОРСТУХ]*$/,
                      min: 6
                    }"
                  />

                  <template slot="error" v-if="errors.has('govTrailerNumber')">
                    {{
                      getValidateErrorWithoutName(
                        errors.first("govTrailerNumber"),
                        "govTrailerNumber"
                      )
                    }}
                  </template>
                </InputLabel>
              </div>
            </div>

            <div class="event-modal-passes-add-form__row flex">
              <div class="event-modal-passes-add-form__row-col _270">
                <InputLabel>
                  <template #label>
                    {{ $t("event.passes.car.vehicleType") }}
                  </template>

                  <Select
                    v-model="addFormData.vehicleType"
                    :options="carTypes"
                    name="vehicleType"
                    v-validate="{
                      required: true,
                      single_dropdown_in: carTypes
                    }"
                  />

                  <template slot="error" v-if="errors.has('vehicleType')">
                    {{ errors.first("vehicleType") }}
                  </template>
                </InputLabel>
              </div>

              <div class="event-modal-passes-add-form__row-col _270">
                <InputLabel>
                  <template #label>
                    {{ $t("event.passes.car.arrivalPurpose") }}
                  </template>

                  <Select
                    v-model="addFormData.arrivalPurpose"
                    :options="arrivalPurposes"
                    name="arrivalPurpose"
                    v-validate="{
                      required: true,
                      single_dropdown_in: arrivalPurposes
                    }"
                  />

                  <template slot="error" v-if="errors.has('arrivalPurpose')">
                    {{ errors.first("arrivalPurpose") }}
                  </template>
                </InputLabel>
              </div>

              <div class="event-modal-passes-add-form__row-col _200">
                <InputLabel>
                  <template #label>
                    {{ $t("event.passes.car.arrivalDate") }}
                  </template>
                  <DatePicker
                    name="arrivalDate"
                    align-right
                    single-date-picker
                    v-model="addFormData.arrivalDate"
                    :available-periods="getServiceAvailablePeriods"
                    data-vv-delay="500"
                    :by-left-edge="true"
                    v-validate="{
                      required: true,
                      single_datepicker_date: true,
                      single_datepicker_greater_or_equal: minAvailableDate,
                      single_datepicker_less_or_equal: dismantingEndDate
                    }"
                  >
                    <template #input="{ visibilityToggler }">
                      <div class="single-datepicker__header">
                        <input
                          type="text"
                          :placeholder="inputPlaceholder"
                          :value="inputValue"
                          @input="onInputChange"
                          v-mask="'##.##.####'"
                          class="single-datepicker__header-input"
                        />

                        <button
                          class="single-datepicker__header-calendar"
                          @click="visibilityToggler()"
                        >
                          <icon name="calendar" />
                        </button>
                      </div>
                    </template>
                  </DatePicker>

                  <template slot="error" v-if="errors.has('arrivalDate')">
                    {{ errors.first("arrivalDate") }}
                  </template>
                </InputLabel>
              </div>
            </div>

            <div class="event-modal-passes-add-form__row flex">
              <div class="event-modal-passes-add-form__row-col _270">
                <Dropdown
                  :value="stands"
                  :title="$t('services.ordering.standSelectTitle')"
                  :preheader="$t('services.ordering.standSelectPreheader')"
                  label-name="title"
                  single
                  modified
                  required
                  without-opacity-title
                  @select="handleStandSelect"
                />
              </div>
              <div class="event-modal-passes-add-form__row-col _270">
                <InputLabel class="form__input">
                  <template #label>
                    {{ $t("services.ordering.pavilion") }}
                  </template>
                  <input v-model="addFormData.pavilion" disabled />
                </InputLabel>
              </div>
              <div class="event-modal-passes-add-form__row-col _200">
                <InputLabel class="form__input">
                  <template #label>
                    {{ $t("services.ordering.hall") }}
                  </template>
                  <input v-model="addFormData.hall" disabled />
                </InputLabel>
              </div>
            </div>

            <h2 class="event-modal-passes-add-form__title">
              {{ $t("event.passes.car.driverApplicationTitle") }}
            </h2>

            <div class="event-modal-passes-add-form__row flex">
              <div class="event-modal-passes-add-form__row-col _380">
                <InputLabel>
                  <template #label>
                    {{ $t("common.lastName") }}
                  </template>

                  <input
                    type="text"
                    v-model="addFormData.lastName"
                    name="lastName"
                    v-validate="'required|custom_alpha|min:1|max:50'"
                  />

                  <template slot="error" v-if="errors.has('lastName')">
                    {{ errors.first("lastName") }}
                  </template>
                </InputLabel>
              </div>

              <div class="event-modal-passes-add-form__row-col _380">
                <InputLabel>
                  <template #label>
                    {{ $t("common.firstName") }}
                  </template>

                  <input
                    type="text"
                    v-model="addFormData.firstName"
                    v-validate="'required|custom_alpha|min:1|max:50'"
                    name="firstName"
                  />

                  <template slot="error" v-if="errors.has('firstName')">
                    {{ errors.first("firstName") }}
                  </template>
                </InputLabel>
              </div>
            </div>

            <div class="event-modal-passes-add-form__row flex">
              <div class="event-modal-passes-add-form__row-col _380">
                <InputLabel>
                  <template #label>
                    {{ $t("common.middleName") }}
                  </template>

                  <input
                    type="text"
                    name="middleName"
                    v-model="addFormData.middleName"
                    v-validate="'custom_alpha|min:1|max:50'"
                  />

                  <template slot="error" v-if="errors.has('middleName')">
                    {{ errors.first("middleName") }}
                  </template>
                </InputLabel>
              </div>

              <div class="event-modal-passes-add-form__row-col _380">
                <InputLabel>
                  <template #label>
                    {{ $t("common.phone") }}
                  </template>

                  <input
                    type="text"
                    v-mask="'+##################'"
                    placeholder="+79000000000"
                    v-model="addFormData.phone"
                    name="phone"
                    v-validate="'required|phone'"
                  />

                  <template slot="error" v-if="errors.has('phone')">
                    {{ errors.first("phone") }}
                  </template>
                </InputLabel>
              </div>
            </div>
            <h2 class="event-modal-passes-add-form__title">
              {{ $t("event.passes.car.cargoApplicationTitle") }}
            </h2>

            <div
              class="event-modal-passes-add-form__row flex"
              v-for="(cargo, cargoIndex) in addFormData.cargos"
              :key="cargo.id"
            >
              <div class="event-modal-passes-add-form__row-col _600">
                <InputLabel>
                  <template #label>
                    {{ $t("event.passes.car.cargoName") }}
                  </template>

                  <input
                    :name="`cargoTitle${cargo.id}`"
                    :placeholder="$t('event.passes.car.cargoNamePlaceholder')"
                    type="text"
                    v-model="cargo.title"
                    v-validate="'required'"
                  />

                  <template
                    slot="error"
                    v-if="errors.has(`cargoTitle${cargo.id}`)"
                  >
                    {{ errors.first(`cargoTitle${cargo.id}`) }}
                  </template>
                </InputLabel>
              </div>

              <div
                class="event-modal-passes-add-form__row-col _160 event-modal-auto-passes__add-cargo"
              >
                <InputLabel>
                  <template #label>
                    {{ $t("event.passes.car.cargoQty") }}
                  </template>

                  <input
                    type="number"
                    placeholder="1"
                    v-model="cargo.quantity"
                    :name="`cargoQty${cargo.id}`"
                    v-validate="'required|numeric'"
                    data-vv-as=" "
                    min="1"
                  />

                  <template
                    slot="error"
                    v-if="errors.has(`cargoQty${cargo.id}`)"
                  >
                    {{ errors.first(`cargoQty${cargo.id}`) }}
                  </template>
                </InputLabel>

                <button
                  v-if="cargoIndex"
                  class="event-modal-auto-passes__add-cargo-delete"
                  @click="deleteCargo(cargo)"
                >
                  <icon name="delete" />
                </button>
              </div>
            </div>

            <div class="event-modal-passes-add-form__row">
              <LinkIcon icon="add" :bold="true" :grey="true" @click="addCargo">
                {{ $t("event.passes.car.addCargo") }}
              </LinkIcon>
            </div>
          </EventModalPassesAddForm>

          <EventModalPassesUsers
            v-if="autoPasses.length"
            type="car"
            :users="autoPasses"
            :deadline="passDeadline.car"
            :is-loading="isLoadingWithOpacity"
            @add="addNewPass"
            @delete="deletePass"
            @delete-all="deleteAllPasses"
            @print="printPass"
            @print-d03="printPassD03"
            @download="downloadPass"
            @download-d03="downloadPassD03"
            @copyAutoPass="pasteAutoPass"
            @upload="updateAutoPasses"
            class="event-modal-auto-passes__users"
          />
          <div class="event-modal-auto-passes__users">
            <pagination
              class="event-modal-auto-passes__pagination"
              v-if="autoPassesPaginationLength > 1"
              :page="currentPage"
              :page-count="autoPassesPaginationLength"
              :total-visible="paginationPageAmount"
              @change="paginate"
            />
          </div>
        </template>
      </div>
    </Modal>
  </global-modal>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import shortId from "shortid";
import moment from "moment";
import printJS from "print-js";
import apiCarPass from "@/api/carPass";
import arrivalPurposes from "@/helpers/constants/arrivalPurposes";
import diffInSeconds from "@/helpers/diffInSeconds";
import DeliveryRules from "@/components/Custom/DeliveryRules.vue";
import {
  LinkIcon,
  Dropdown,
  Loader,
  Pagination,
  InputLabel,
  Select,
  DatePicker
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import { getLocaleValue } from "@/helpers/common";
import EventModalPassesHeader from "./EventModalPassesHeader.vue";
import EventModalPassesAddForm from "./EventModalPassesAddForm.vue";
import EventModalPassesUsers from "./EventModalPassesUsers.vue";
import Modal from "../Modal.vue";
import getError from "@/helpers/error";
import store from "@/store";

export default {
  components: {
    Pagination,
    Dropdown,
    Loader,
    LinkIcon,
    Select,
    InputLabel,
    Modal,
    DatePicker,
    EventModalPassesHeader,
    EventModalPassesAddForm,
    EventModalPassesUsers,

    DeliveryRules
  },

  data() {
    return {
      isLoading: false,
      isLoadingWithOpacity: false,
      isRulesOpen: false,
      isAddFormShown: false,
      isAddFormSubmitting: false,
      carTypes: [],
      arrivalPurposes: [],
      currentPage: 1,
      addFormData: {
        govVehicleNumber: "".toUpperCase(),
        govTrailerNumber: "".toUpperCase(),

        arrivalDate: null,
        departureDate: null,
        carryingCapacity: "",

        vehicleType: "",
        arrivalPurpose: "",

        pavilion: "",
        hall: "",
        stand: "",

        lastName: "",
        firstName: "",
        middleName: "",

        phone: "",

        cargos: []
      },
      minDate: "",
      maxDate: "",

      addFormError: "",
      govTrailerNumberFilter: {
        F: {
          pattern: /[0-9a-zA-ZА-Яа-я ]/,
          transform: v => v.toLocaleUpperCase()
        }
      }
    };
  },

  computed: {
    ...mapState("event", ["event"]),
    ...mapState("passes", [
      "autoPasses",
      "autoPassesPaginationLength",
      "autoPassesAmount"
    ]),

    ...mapGetters("event", [
      "hasAccessToWrite",
      "eventId",
      "uniquePavilion",
      "passDeadline",
      "eventPavilion",
      "isEventMontage"
    ]),
    exhibition() {
      return this.event ? this.event.exhibition : {};
    },
    minAvailableDate() {
      return moment(this.exhibition.installation_started_at) > moment()
        ? moment(this.exhibition.installation_started_at)
        : moment();
    },
    inputPlaceholder() {
      return this.locale === "en" ? "DD.MM.YYYY" : "ДД.ММ.ГГГГ";
    },
    paginationPageAmount() {
      return window.innerWidth > 570 ? 5 : 2;
    },
    getServiceAvailablePeriods() {
      let dates = [];
      dates.push({
        startDate: this.exhibition.installation_started_at,
        endDate: this.exhibition.installation_ended_at,
        type: "installation"
      });
      dates.push({
        startDate: this.exhibition.started_at,
        endDate: this.exhibition.ended_at,
        type: "exhibition"
      });
      dates.push({
        startDate: this.exhibition.dismantling_started_at,
        endDate: this.exhibition.dismantling_ended_at,
        type: "dismantling"
      });
      return dates;
    },
    dismantingEndDate() {
      let date;

      if (this.isEventMontage) {
        if (this.passDeadline && this.passDeadline.endDismantling) {
          date = this.passDeadline.endDismantling;
        }
      } else if (this.passDeadline.endDismantling) {
        date = this.passDeadline.endDismantling;
      }

      return moment(date);
    },

    stands() {
      return (
        this.event?.stands?.map(stand => {
          return {
            ...stand,
            selected: stand.id === this.addFormData?.stand?.id
          };
        }) || []
      );
    },

    locale() {
      return i18n.locale;
    },
    inputValue() {
      return this.addFormData.arrivalDate
        ? this.addFormData.arrivalDate.format("DD.MM.YYYY")
        : "";
    },
    header() {
      return {
        quota: {
          count: this.autoPassesAmount
        },
        deadline: this.passDeadline.car,
        passes: this.autoPasses
      };
    },
    isShowRules() {
      return (
        Boolean(
          Number(getLocaleValue(this.exhibition, "create_car_pass_disabled"))
        ) || false
      );
    },
    transformedAddFormData() {
      return {
        car_type_id: this.addFormData.vehicleType,
        car_number: this.addFormData.govVehicleNumber,
        trailer_number: this.addFormData.govTrailerNumber,
        entry_date: moment(this.addFormData.arrivalDate).format("YYYY-MM-DD"),
        departure_date: moment(this.addFormData.departureDate).format(
          "YYYY-MM-DD"
        ),
        load_capacity: parseFloat(this.addFormData.carryingCapacity),
        target: this.addFormData.arrivalPurpose,
        stand_id: this.addFormData.stand?.id,
        lastname: this.addFormData.lastName,
        name: this.addFormData.firstName,
        middlename: this.addFormData.middleName,
        phone: this.addFormData.phone,
        cargos: this.addFormData.cargos.map(cargItem => ({
          title: cargItem.title,
          quantity: cargItem.quantity
        }))
      };
    }
  },

  methods: {
    getLocaleValue,
    pasteAutoPass(pass) {
      this.isAddFormShown = true;
      this.addFormData.govVehicleNumber = pass.car_number;
      this.addFormData.govTrailerNumber = pass.trailer_number;
      this.addFormData.arrivalDate = moment(pass.entry_date);
      this.addFormData.vehicleType = pass.car_type.id;
      this.addFormData.arrivalPurpose = pass.target;
      this.addFormData.lastName = pass.lastname;
      this.addFormData.firstName = pass.name;
      this.addFormData.middleName = pass.middlename;
      this.addFormData.phone = pass.phone;
      this.addFormData.cargos = [...pass.cargos];

      const stand = this.stands.find(stand => stand.title === pass.stand);
      if (stand) {
        this.handleStandSelect(stand);
      }
    },
    getValidateErrorWithoutName(error, name) {
      return error.replace(`${name} `, "");
    },

    diffInSeconds,
    async updateAutoPasses() {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/fetchAutoPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoadingWithOpacity = false;
    },
    async paginate(page) {
      this.isLoadingWithOpacity = true;
      this.currentPage = page;
      await this.$store.dispatch("passes/fetchAutoPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoadingWithOpacity = false;
    },
    async beforeOpen() {
      if (this.stands.length === 1) {
        this.handleStandSelect(this.stands[0]);
      }
      this.isLoading = true;
      await this.$store.dispatch("passes/fetchAutoPasses", {
        eventId: this.eventId
      });
      this.isLoading = false;
      await this.fetchCarTypes();
    },
    handleStandSelect(stand) {
      if (stand) {
        this.addFormData.stand = stand;
        this.addFormData.pavilion = this.$options.filters.localeField(
          stand.pavilion.parent,
          "title"
        );
        this.addFormData.hall = this.$options.filters.localeField(
          stand.pavilion,
          "title"
        );
      }
    },
    async fetchCarTypes() {
      const response = await apiCarPass.getCarTypes();
      if (response && response.data) {
        this.carTypes = response.data.map(item => ({
          value: item.id,
          title: this.locale === "en" ? item.title_eng : item.title_rus
        }));
      }
    },
    onInputChange(e) {
      const dateMask = "DD.MM.YYYY";
      let startDate = e.target.value;
      if (startDate && startDate.length === dateMask.length) {
        startDate = moment(startDate, dateMask);
        this.addFormData.arrivalDate = startDate.isValid() ? startDate : null;
      } else {
        this.addFormData.arrivalDate = null;
      }
    },

    addCargo() {
      this.addFormData.cargos.push({
        id: shortId.generate(),
        title: "",
        quantity: null
      });
    },

    deleteCargo(cargo) {
      const cargoIndex = this.addFormData.cargos.findIndex(
        c => c.id === cargo.id
      );

      if (cargoIndex > -1) {
        this.addFormData.cargos.splice(cargoIndex, 1);
      }
    },

    submit() {
      this.$validator.validate().then(isValid => {
        if (!isValid) {
          return;
        }
        this.isAddFormSubmitting = true;

        this.$store
          .dispatch("passes/addAutoPass", {
            eventId: this.event.id,
            pass: this.transformedAddFormData
          })
          .then(() => {
            this.$validator.reset();
            this.$validator.pause();

            this.isAddFormSubmitting = false;
            this.isAddFormShown = false;
            this.reset();
          })
          .catch(err => {
            store.dispatch("common/setErrorMessage", getError(err));
            this.$validator.pause();
            this.isAddFormSubmitting = false;
          });
      });
    },

    async deletePass(pass) {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/removeAutoPass", {
        eventId: this.eventId,
        passId: pass.id
      });
      this.isLoadingWithOpacity = false;
    },

    async deleteAllPasses() {
      this.isLoading = true;
      await this.$store.dispatch("passes/removeAllAutoPasses", {
        eventId: this.eventId
      });
      this.isLoading = false;
    },

    reset() {
      this.addFormData = {
        govVehicleNumber: "",
        govTrailerNumber: "",

        arrivalDate: null,

        vehicleType: "",
        arrivalPurpose: "",

        lastName: "",
        firstName: "",
        middleName: "",

        phone: "",

        cargos: [],

        stand: {}
      };

      this.addCargo();
    },

    printPass(pass) {
      this.downloadPass(pass, true);
    },

    printPassD03(pass) {
      this.downloadPassD03(pass, true);
    },

    async downloadPass(pass, print = false) {
      this.isLoading = true;
      const response = await apiCarPass.download(this.eventId, pass.id);
      let blob = new Blob([response], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      if (print) {
        printJS(url);
      } else {
        let link = document.createElement("a");
        link.href = url;
        link.download = `Разовый автомобильный пропуск.pdf`;
        link.click();
      }
      this.isLoading = false;
    },

    async downloadPassD03(pass, print = false) {
      this.isLoading = true;
      const response = await apiCarPass.downloadD03(this.eventId, pass.id);
      let blob = new Blob([response], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      if (print) {
        printJS(url);
      } else {
        let link = document.createElement("a");
        link.href = url;
        link.download = `Форма D03.pdf`;
        link.click();
      }
      this.isLoading = false;
    },

    printAllPasses() {},

    downloadAllPasses() {},

    addNewPass() {
      this.isAddFormShown = true;
      this.$nextTick(() => {
        this.$validator.reset();
        this.$validator.resume();
      });
    },

    setArrivalPurposesOptions() {
      this.arrivalPurposes = arrivalPurposes.map(item => {
        return {
          value: item.value,
          title: this.locale === "en" ? item.title_eng : item.title_rus
        };
      });
    }
  },

  created() {
    if (!this.addFormData.cargos.length) {
      this.addCargo();
    }
    this.setArrivalPurposesOptions();
  },
  watch: {
    locale() {
      this.setArrivalPurposesOptions();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-auto-passes {
  &__pagination {
    margin: 30px auto 30px;
    @include respond-down(lg) {
      margin: 20px auto 20px;
    }
  }
  &__add {
    background-color: rgba(#f9f9f9, 0.5);

    border-top: 2px solid #f1f1f1;
    border-bottom: 2px solid #f1f1f1;

    &-cargo {
      display: flex;
      align-items: flex-end;

      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        margin: 0;
      }

      input[type="number"] {
        -moz-appearance: textfield;
      }

      &-delete {
        @include button-clean();

        margin: 0 0 15px 20px;

        .svg-icon {
          width: 16px;
          height: 16px;
        }
      }
    }
  }

  &__users {
    width: 780px;
    margin-left: 80px;

    @include respond-down(lg) {
      margin: 0;
      width: 100%;
      padding: 0 24px;
      box-sizing: border-box;
    }

    @include respond-down(md) {
      margin: 0;
      padding: 0 0 30px 0;
    }
  }

  &__add + &__users:not(:empty) {
    margin-top: 40px;
  }
}
.loader-wrap {
  position: relative;
  min-height: 200px;
  max-width: 870px;
}
</style>
